import { isPasswordComplexEnough } from "@autorepair/contracts";
import { withBase } from "../lib/api";
import { useLoginFlow } from "../lib/useLoginFlow";
import { DevEnvironmentBanner } from "../components/ui-modern";
import {
  ModernButton,
  ModernErrorBanner,
  ModernField,
  ModernFooter,
  ModernInput,
  ModernPasswordInput,
  ModernPasswordRequirementsList,
  ModernPasswordStrengthMeter,
} from "../components/ui-modern";

const STEPS = ["Build the estimate", "Assign and track the work", "Invoice and close"];

// Dev/media-pc overrides this via VITE_MARKETING_URL (see .env.example);
// production leaves it unset and gets the real marketing site directly —
// the first client-exposed custom VITE_ var in this codebase.
const MARKETING_URL = import.meta.env.VITE_MARKETING_URL || "https://bay2bill.com";

/**
 * The modern theme's Login screen — see
 * tmp/design_handoff_bay2bill_redesign/README.md and
 * `Bay2Bill Redesign.dc.html` lines ~817-843 ("isLogin" block): a "brand"
 * card on the left (logo + headline + a 3-step list) and a "Sign in" card
 * on the right, the two centered in a wrapping flex row.
 *
 * Two deliberate deviations from the mockup's literal navy-bleed hero
 * (owner's own call, not a mockup-fidelity bug): the page background here
 * is white (mockup: full-bleed navy `#071a33`), and the sign-in card is
 * the app's own light-grey panel tone `#f6f8fb` (mockup: white, same as
 * the brand card) — the brand card stays white either way. The footer
 * ("Bay2Bill is operated by Scanvato Labs LLC.") is the SAME shared
 * `ModernFooter` every authenticated route gets via AppShellModern.tsx
 * (two lines, `border-t`), not the mockup's single inline line inside the
 * sign-in card — so it reads identically whether a shop is signed in or
 * not.
 *
 * `LoginPage.tsx` renders this unconditionally — Login isn't scoped to a
 * tenant's `uiTheme` (no session/tenant exists yet at this point anyway);
 * the owner's own call is that the pre-auth screen looks modern regardless
 * of which theme any given shop is on once signed in. All real auth logic
 * comes from `useLoginFlow()` (shared with nothing on the classic side),
 * so this never risks reimplementing login with subtly different behavior.
 */
export function LoginPageModern() {
  const flow = useLoginFlow();

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <DevEnvironmentBanner />
      <div className="px-5 pt-5">
        <a href={MARKETING_URL} className="text-[13.5px] font-semibold text-[#0f6d86] no-underline hover:underline">
          ← Back to bay2bill.com
        </a>
      </div>
      <div className="flex flex-1 flex-wrap items-center justify-center gap-7 px-5 py-14">
        <div className="w-full max-w-[440px] flex-1 basis-80 rounded-2xl border border-[#e2e7ee] bg-white p-8 text-[#101b2d]">
          <img src={withBase("/bay2bill-logo.jpg")} alt="Bay2Bill — Auto Repair Easy Invoicing" className="mb-5 h-16 w-auto" />
          <h1 className="m-0 text-[31px] font-bold leading-[1.2] tracking-[-.01em] text-[#071a33]">
            Run the repair.
            <br />
            We&apos;ll handle the bill.
          </h1>
          <p className="mt-3.5 max-w-[400px] text-[15px] leading-relaxed text-[#4a5361]">
            Customers, vehicles, estimates, work orders, technicians and invoices in one place.
          </p>
          <ul className="mt-5 flex list-none flex-col gap-2.5 p-0 text-sm text-[#4a5361]">
            {STEPS.map((step, i) => (
              <li key={step} className="flex items-center gap-2.5">
                <span className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full bg-[#e6f1f5] text-[11px] font-bold text-[#0f6d86]">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ul>
        </div>

        <div className="w-full max-w-[400px] flex-1 basis-80 rounded-2xl bg-[#f6f8fb] p-6.5 shadow-[0_20px_50px_rgba(0,0,0,.35)]">
          <h2 className="m-0 mb-5 text-[19px] font-bold text-[#071a33]">Sign in</h2>

          {flow.error && <ModernErrorBanner message={flow.error} />}

          {flow.needsPasswordChange ? (
            <form onSubmit={(e) => void flow.handleChangePasswordFirstLogin(e)}>
              <p className="mb-4 text-sm text-[#4a5361]">
                This account was set up with a temporary password. Choose a new one to continue.
              </p>
              <ModernField label="New Password" htmlFor="m-newPassword" required>
                <ModernPasswordInput
                  id="m-newPassword"
                  autoComplete="new-password"
                  autoFocus
                  required
                  value={flow.newPassword}
                  onChange={(e) => flow.setNewPassword(e.target.value)}
                />
                <ModernPasswordRequirementsList password={flow.newPassword} />
                <ModernPasswordStrengthMeter password={flow.newPassword} />
              </ModernField>
              <ModernField label="Confirm New Password" htmlFor="m-confirmNewPassword" required>
                <ModernPasswordInput
                  id="m-confirmNewPassword"
                  autoComplete="new-password"
                  required
                  value={flow.confirmNewPassword}
                  onChange={(e) => flow.setConfirmNewPassword(e.target.value)}
                />
              </ModernField>
              <ModernButton
                type="submit"
                className="w-full"
                disabled={flow.submitting || !isPasswordComplexEnough(flow.newPassword) || flow.newPassword !== flow.confirmNewPassword}
              >
                {flow.submitting ? "Saving…" : "Set New Password"}
              </ModernButton>
            </form>
          ) : flow.needsTotp ? (
            <form onSubmit={(e) => void flow.handleVerifyTotp(e)}>
              <p className="mb-4 text-sm text-[#4a5361]">Enter the code from your authenticator app.</p>
              <ModernField label="Code" htmlFor="m-totpCode" required>
                <ModernInput
                  id="m-totpCode"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={flow.totpCode}
                  onChange={(e) => flow.setTotpCode(e.target.value)}
                />
              </ModernField>
              <label className="mb-4 flex items-center gap-2 text-[13px] text-[#4a5361]">
                <input
                  type="checkbox"
                  checked={flow.rememberDevice}
                  onChange={(e) => flow.setRememberDevice(e.target.checked)}
                  className="h-4 w-4 rounded border border-[#e2e7ee]"
                />
                Remember this device
              </label>
              <ModernButton type="submit" className="w-full" disabled={flow.submitting}>
                {flow.submitting ? "Verifying…" : "Verify"}
              </ModernButton>
            </form>
          ) : flow.tenants ? (
            <div>
              <p className="mb-4 text-sm text-[#4a5361]">Which shop would you like to open?</p>
              <div className="flex flex-col gap-2.5">
                {flow.tenants.map((t) => (
                  <ModernButton key={t.tenantId} type="button" onClick={() => void flow.pickTenant(t.tenantId)} disabled={flow.submitting}>
                    {t.displayName}
                  </ModernButton>
                ))}
              </div>
            </div>
          ) : (
            <form onSubmit={(e) => void flow.handleSubmit(e)}>
              <ModernField label="Email" htmlFor="m-email" required>
                <ModernInput
                  id="m-email"
                  type="email"
                  placeholder="you@shop.com"
                  autoComplete="username"
                  required
                  value={flow.email}
                  onChange={(e) => flow.setEmail(e.target.value)}
                />
              </ModernField>
              <ModernField label="Password" htmlFor="m-password" className="mb-4.5" required>
                <div className="flex items-center gap-2">
                  <ModernInput
                    id="m-password"
                    type={flow.showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    value={flow.password}
                    onChange={(e) => flow.setPassword(e.target.value)}
                    className="flex-1"
                  />
                  <ModernButton
                    type="button"
                    variant="secondary"
                    className="h-[46px] flex-none px-3.5"
                    onClick={() => flow.setShowPassword((s) => !s)}
                  >
                    {flow.showPassword ? "Hide" : "Show"}
                  </ModernButton>
                </div>
              </ModernField>
              <ModernButton type="submit" className="w-full" disabled={flow.submitting}>
                {flow.submitting ? "Signing in…" : "Sign in"}
              </ModernButton>
            </form>
          )}
        </div>
      </div>
      <ModernFooter />
    </div>
  );
}
